import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { BookOpen, ArrowLeft, Loader2, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";

const LectureNotesGenerator = () => {
  const { bookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [detailLevel, setDetailLevel] = useState(50);
  const [maxPages, setMaxPages] = useState(10);
  const [creditCosts, setCreditCosts] = useState({ lecture_notes: 5 });
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const pollIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const hasCompletedRef = useRef(false);
  
  const chapterIds = searchParams.get('chapters')?.split(',') || [];

  useEffect(() => {
    fetchBook();
    axios.get(`${API}/credits/costs`)
      .then(res => setCreditCosts(res.data))
      .catch(err => console.error("Failed to fetch costs:", err));
    
    return () => {
      if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [bookId]);

  const fetchBook = async () => {
    try {
      const response = await axios.get(`${API}/books/${bookId}`, {
        headers: getAuthHeaders(token)
      });
      setBook(response.data);
    } catch (error) {
      toast.error("Failed to load book");
      navigate("/books");
    } finally {
      setLoading(false);
    }
  };

  const startProgressAnimation = () => {
    setProgress(5);
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        // Slowly increase progress, max out at 90% until complete
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 90) return prev + 0.5;
        return prev;
      });
    }, 500);
  };

  const stopProgressAnimation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleGenerate = async () => {
    if (chapterIds.length === 0) {
      toast.error("No chapters selected");
      return;
    }

    const noteCost = creditCosts.lecture_notes || 5;
    if ((user?.credits || 0) < noteCost) {
      toast.error(`Insufficient credits. You need ${noteCost} credits for lecture notes.`);
      navigate("/pricing");
      return;
    }

    // Reset state
    setGenerating(true);
    setProgress(0);
    setStatusMessage("Starting generation...");
    hasCompletedRef.current = false;
    
    try {
      const response = await axios.post(`${API}/lecture-notes/generate`, {
        book_id: bookId,
        chapter_ids: chapterIds,
        detail_level: detailLevel,
        max_pages: maxPages
      }, {
        headers: getAuthHeaders(token),
        timeout: 15000 // Short timeout - endpoint should return quickly
      });

      const notesId = response.data.notes_id;
      setStatusMessage("Generating lecture notes...");
      startProgressAnimation();
      
      // Poll for status using recursive setTimeout (more reliable than setInterval)
      const pollStatus = async () => {
        // Prevent multiple completions
        if (hasCompletedRef.current) return;
        
        try {
          const statusRes = await axios.get(`${API}/lecture-notes/${notesId}`, {
            headers: getAuthHeaders(token)
          });
          
          console.log("Poll status:", statusRes.data.status);
          
          if (statusRes.data.status === "ready") {
            hasCompletedRef.current = true;
            stopProgressAnimation();
            setProgress(100);
            setStatusMessage("Complete!");
            await refreshUser();
            toast.success("Lecture notes generated successfully!");
            setTimeout(() => {
              navigate(`/lecture-notes/${notesId}`);
            }, 500);
          } else if (statusRes.data.status === "error") {
            hasCompletedRef.current = true;
            stopProgressAnimation();
            setGenerating(false);
            setProgress(0);
            toast.error(statusRes.data.error || "Generation failed. Please try again.");
          } else {
            // Still processing, poll again in 2 seconds
            pollIntervalRef.current = setTimeout(pollStatus, 2000);
          }
        } catch (err) {
          console.error("Error polling notes status:", err);
          // On error, try again
          pollIntervalRef.current = setTimeout(pollStatus, 3000);
        }
      };
      
      // Start polling after a short delay
      pollIntervalRef.current = setTimeout(pollStatus, 2000);
      
      // Safety timeout: after 3 minutes, stop and show message
      setTimeout(() => {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
          stopProgressAnimation();
          setGenerating(false);
          setProgress(0);
          toast.info("Taking longer than expected. Check your lecture notes to see if generation completed.");
        }
      }, 180000); // 3 minutes

    } catch (error) {
      stopProgressAnimation();
      setGenerating(false);
      setProgress(0);
      
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Please try again.");
      } else if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate lecture notes");
      }
    }
  };

  const selectedChapters = book?.chapters?.filter(ch => chapterIds.includes(ch.chapter_id)) || [];

  const getDetailLabel = () => {
    if (detailLevel < 30) return "Brief & Concise";
    if (detailLevel < 70) return "Moderately Detailed";
    return "Comprehensive & Detailed";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lecture-notes-generator">
      <DashboardHeader />

      <main className="container-main py-8 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Generate Lecture Notes
            </h1>
            <p className="text-slate-600">{book?.title}</p>
          </div>
        </div>

        {/* Selected Chapters */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#4F46E5]" />
              Selected Chapters ({selectedChapters.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {selectedChapters.map((chapter, idx) => (
                <div key={chapter.chapter_id} className="p-3 bg-indigo-50 rounded-lg">
                  <span className="text-xs font-medium text-[#4F46E5]">Chapter {idx + 1}</span>
                  <p className="font-medium text-slate-900">{chapter.title}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#F97316]" />
              Notes Configuration
            </CardTitle>
            <CardDescription>
              Customize how detailed your lecture notes should be
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Detail Level */}
            <div>
              <div className="flex justify-between mb-3">
                <Label>Detail Level</Label>
                <span className="text-sm font-medium text-[#4F46E5]">{getDetailLabel()}</span>
              </div>
              <Slider
                value={[detailLevel]}
                onValueChange={(value) => setDetailLevel(value[0])}
                max={100}
                step={1}
                className="mb-2"
                disabled={generating}
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>Less Analytic</span>
                <span>Much Analytic</span>
              </div>
            </div>

            {/* Max Pages */}
            <div>
              <Label className="mb-2 block">Maximum Pages</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={maxPages}
                  onChange={(e) => setMaxPages(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-24"
                  disabled={generating}
                />
                <span className="text-sm text-slate-500">pages (approximately {maxPages * 500} words)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generate Button */}
        <Card className="card-base bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="p-6">
            {/* Progress indicator when generating */}
            {generating && (
              <div className="mb-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-700 font-medium">{statusMessage}</span>
                  <span className="text-indigo-600 font-medium">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-slate-500">
                  This may take up to a minute depending on the content length.
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h4 className="font-semibold text-slate-900">Ready to Generate</h4>
                <p className="text-sm text-slate-600">
                  This will create detailed lecture notes from {selectedChapters.length} chapter{selectedChapters.length > 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Cost</p>
                  <p className="text-lg font-bold text-[#4F46E5]">{creditCosts.lecture_notes || 5} credits</p>
                </div>
                
                <Button
                  onClick={handleGenerate}
                  disabled={generating || selectedChapters.length === 0}
                  className="btn-primary h-12 px-8 gap-2"
                  data-testid="generate-notes-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5" />
                      Generate Notes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LectureNotesGenerator;
